/**
 * @polst-web/widget — public entry point.
 *
 * This is the surface the script/SDK embedding modes consume. It is
 * intentionally kept small and stable. Later tickets (POL-581, POL-582)
 * will replace the stub implementations behind these exports without
 * changing the surface.
 */

export { PolstApiError } from "./errors";
export type {
  BrandFeedItem as BrandFeedApiItem,
  BrandListResponse,
  CampaignProgress,
  CampaignSummary,
  ClientOptions,
  PolstResults,
  PolstSummary,
  VoteOption,
  VoteResult,
} from "./api";
export {
  configureRestClient,
  getPolst,
  getPolstResults,
  vote,
  votePolst,
  getCampaign,
  getCampaignProgress,
  voteCampaign,
  getBrandPolsts,
  getBrandCampaigns,
  getBrandFeed,
} from "./api";
export {
  DEFAULT_BASE_URL,
  DEVICE_ID_STORAGE_KEY,
  RestClient,
} from "./rest-client";
export type {
  RestClientOptions,
  RequestOptions,
  RetryPolicy,
  StorageLike,
} from "./rest-client";
export { mount } from "./mount";
export type { MountOptions, MountHandle } from "./mount";
export { createRoot } from "./shadow-root";
export type { CreateRootOptions, ShadowRootBundle } from "./shadow-root";
export {
  SHADOW_CSS,
  THEME_TOKENS,
  normalizeThemeMode,
  BRAND_RADIUS_PX,
  BRAND_DENSITY_SCALES,
  BRAND_FONT_STACKS,
} from "./theme";
export type { ThemeMode, ThemeTokenName } from "./theme";
export {
  applyBrandTheme,
  applyHostAccentOverride,
  effectiveColorScheme,
  mapBrandThemeToCssVars,
  resolveLogoUrl,
  sanitizeBrandCustomCss,
  watchColorScheme,
  BRAND_CUSTOM_CSS_MAX_BYTES,
} from "./brand-theme";
export type { BrandThemeDTO } from "./brand-theme";
export { renderPolstCard } from "./polst-card";
export type {
  PolstCardDTO,
  PolstCardOptions,
  PolstCardState,
  PolstCardStatus,
  PolstHandle,
} from "./polst-card";
export { renderCampaignPlayer } from "./campaign-player";
export type {
  CampaignPlayerDTO,
  CampaignPlayerHandle,
  CampaignPlayerOptions,
  CampaignPlayerProgress,
  CampaignPlayerState,
  CampaignPlayerStatus,
  CampaignStepVoteEvent,
  CampaignCompleteEvent,
} from "./campaign-player";
export { renderBrandFeed } from "./brand-feed";
export type {
  BrandFeedCardClickEvent,
  BrandFeedHandle,
  BrandFeedItem,
  BrandFeedMixedItem,
  BrandFeedMode,
  BrandFeedOptions,
  BrandFeedPage,
  BrandFeedPageLoadedEvent,
  BrandFeedState,
  BrandFeedStatus,
} from "./brand-feed";
// Auto-hydration scanner (POL-587). Importing this module for its
// side effect installs the `DOMContentLoaded` bootstrap so marker
// elements (and the loading `<script>` itself) are hydrated without
// any additional code from the host page.
export {
  autoHydrate,
  installAutoHydrateBootstrap,
  installMutationObserver,
  isHydrated,
  mountAutoTarget,
  readConfig,
} from "./auto-hydrate";
export type {
  AutoHydrateConfig,
  MountAutoHandle,
  MountAutoKind,
  MountAutoTargetOptions,
} from "./auto-hydrate";
// `window.Polst` global (POL-588). Importing this module for its
// side effect installs the global in browser contexts. Under SSR the
// installer short-circuits on the `typeof window` guard.
export { createWindowPolst, installGlobal, mountGlobal } from "./global";
export type {
  MountGlobalHandle,
  MountGlobalOptions,
  WindowPolst,
  WindowPolstConfigureOptions,
} from "./global";
// Origin configuration (POL-718). Build-time defaults bake the
// production polst.app hosts into the bundle; runtime overrides via
// `configureOrigins()` (or `window.Polst.configure({ origins })`) win.
export {
  configureOrigins,
  DEFAULT_API_ORIGIN,
  DEFAULT_APP_ORIGIN,
  getApiOrigin,
  getAppOrigin,
} from "./config";
export type { OriginOverrides } from "./config";
// Analytics emitter (POL-589). Fire-and-forget events (view/vote/
// complete) are wired into the shared mount path, so hosts don't
// need to import anything to receive them. The type exports are
// surfaced so the native SDK (POL-558) and tooling can share the
// wire shape.
export {
  configureSharedAnalyticsUrl,
  createAnalyticsEmitter,
  DEFAULT_ANALYTICS_URL,
  getSharedEmitter,
} from "./analytics";
export type {
  AnalyticsEmitter,
  AnalyticsEmitterOptions,
  EmbedEvent,
  EmbedEventContext,
  EmbedEventInput,
  EmbedEventType,
} from "./analytics";

/** Package version — injected at build time via esbuild `define`. */
export const VERSION: string =
  typeof __POLST_WIDGET_VERSION__ !== "undefined" ? __POLST_WIDGET_VERSION__ : "0.0.0";

declare const __POLST_WIDGET_VERSION__: string;
